/**
 * Pannello dell'assistente (V58): la conversazione sopra la vista aperta.
 *
 * Il contesto — squadra-stagione e vista — si ricava dal percorso e parte con
 * ogni domanda, cosi "chi ha giocato di piu?" vale sulla squadra che si sta
 * guardando (`contestoDomanda.ts`).
 *
 * La conversazione sta nel provider, quindi chiudere il pannello non la
 * perde e la si ritrova riaprendolo, anche dopo essere passati dalla vista a
 * tutta pagina.
 *
 * L'interruttore delle letture (l'icona del registro) mostra sotto le risposte le funzioni chiamate e i dati letti:
 * spento di serie, perche in una demo sono rumore.
 */

import { useLocation, useNavigate } from 'react-router-dom';
import {
  Maximize2Icon,
  MessagesSquareIcon,
  PlusIcon,
  ScrollTextIcon,
  SparklesIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAssistente } from './contesto.js';
import { useContestoDomanda, useNuovaConversazione } from './contestoDomanda.js';
import { Dialogo } from './Dialogo.js';

export function PannelloAssistente({
  aperto,
  onCambia,
  domande,
}: {
  aperto: boolean;
  onCambia: (aperto: boolean) => void;
  domande: { usate: number; massimo: number };
}) {
  const { conversazione, mostraLetture, impostaLetture } = useAssistente();
  const { contesto, nomeSquadra, titoloVista } = useContestoDomanda();
  const nuova = useNuovaConversazione();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Anche una conversazione appena cominciata va a tutta pagina: finché la
  // prima domanda non le dà un id, la sua pagina è quella della nuova.
  const tuttaPagina = `/assistente/${conversazione.id ?? 'nuova'}`;

  const vai = (percorso: string) => {
    onCambia(false);
    void navigate(percorso);
  };

  return (
    <Sheet open={aperto} onOpenChange={onCambia}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        // Le larghezze di serie del pannello sono per lato: si sovrascrivono
        // con lo stesso selettore, altrimenti vincono loro.
        className="flex flex-col gap-0 border-l-2 border-foreground p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-md"
      >
        <SheetHeader className="gap-1 border-b-2 border-foreground bg-testata p-4">
          <SheetTitle className="flex items-center gap-2 text-lg">
            <SparklesIcon aria-hidden className="size-5" />
            Assistente
          </SheetTitle>
          <p className="text-sm text-muted-foreground">
            {nomeSquadra}
            {titoloVista && ` · ${titoloVista}`}
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {conversazione.messaggi.length > 0 && (
              <Button size="sm" onClick={nuova}>
                <PlusIcon data-icon="inline-start" />
                Nuova
              </Button>
            )}
            {pathname !== tuttaPagina && (
              <Button variant="outline" size="sm" onClick={() => vai(tuttaPagina)}>
                <Maximize2Icon data-icon="inline-start" />A tutta pagina
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => vai('/assistente')}>
              <MessagesSquareIcon data-icon="inline-start" />
              Conversazioni
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  variant="outline"
                  size="sm"
                  className="ml-auto bg-background"
                  pressed={mostraLetture}
                  onPressedChange={impostaLetture}
                  aria-label="Mostra le letture"
                >
                  <ScrollTextIcon />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>
                {mostraLetture ? 'Nascondi le letture' : 'Mostra le letture'}
              </TooltipContent>
            </Tooltip>
          </div>
        </SheetHeader>

        <Dialogo contesto={contesto} domande={domande} onVai={() => onCambia(false)} />
      </SheetContent>
    </Sheet>
  );
}
